import { ValidationErrors } from 'final-form';
import arrayMutators from 'final-form-arrays';
import React, { ReactNode, FunctionComponent } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  UploadMediaAndTranslationsTask,
  uploadMediaAndTranslationsTaskFactory,
} from '~admin/interactor/media/upload-media-and-translations';
import { MediaData } from '~admin/model/media-data';
import { getMediaUploadInitialValues } from '~admin/view/components/create-event/helper/create-event-initial-values.helper';
import { MediaFileField } from '~admin/view/components/ui/form/fields/field-media-file.component';
import { TextField } from '~common/components/form/field-text.component';
import {
  TranslationField,
  TranslationFormData,
} from '~admin/view/components/ui/form/fields/field-translation.component';
import { MediaType } from '~client/model/media-type';
import { Box } from '~common/components/box.component';
import { FormButton } from '~common/components/button/form-button.component';
import { Column } from '~common/components/column.component';
import { FormFields } from '~common/components/form/form-fields.component';
import { useSuccessReaction } from '~common/hook/use-reaction';
import { useTask } from '~common/hook/use-task';
import { validate } from '~common/validator';
import { required, urlPartialValidation } from '~common/validator/validations';
import { NoScrollBar } from '~web/view/theme/utils';

interface Props {
  mediaType: MediaType;
  onFileUploaded?: (media: MediaData) => void;
}

export interface MediaUploadFormData {
  file: TranslationFormData<File>[];
  folderName: string;
  description: string;
  width: number;
  height: number;
}

interface MediaUploadFormProps extends FormRenderProps<MediaUploadFormData> {
  task: UploadMediaAndTranslationsTask;
  mediaType: MediaType;
}

const MediaUploadForm: FunctionComponent<MediaUploadFormProps> = ({
  task,
  form,
  mediaType,
  handleSubmit,
  invalid,
}) => {
  const { t } = useTranslation();

  const onMediaSelected = (width: number, height: number): void => {
    form.change('width', width);
    form.change('height', height);
  };

  return (
    <Container onSubmit={handleSubmit}>
      <FieldColumn>
        <TranslationField
          name={'file'}
          label={t('form.label.fileDefault')}
          render={(renderProps): ReactNode => (
            <MediaFileField
              mediaType={mediaType}
              onMediaSelected={onMediaSelected}
              {...renderProps}
            />
          )}
        />
      </FieldColumn>
      <FieldColumn>
        <TextField
          name={'folderName'}
          label={t('createEvent.eventDetails.media.overlay.upload.folderName')}
        />
      </FieldColumn>
      <FieldColumn>
        <TextField
          name={'description'}
          label={t('createEvent.eventDetails.media.overlay.upload.description')}
        />
      </FieldColumn>
      <FormButtonContainer>
        <FormButton fullWidth isInvalid={invalid} task={task}>
          {t('createEvent.eventDetails.media.overlay.upload.button')}
        </FormButton>
      </FormButtonContainer>
    </Container>
  );
};

export const MediaPickerUpload: FunctionComponent<Props> = ({ mediaType, onFileUploaded }) => {
  const uploadMediaAndTranslationsTask = useTask(uploadMediaAndTranslationsTaskFactory);

  useSuccessReaction(uploadMediaAndTranslationsTask, (result: MediaData): void => {
    onFileUploaded?.(result);
  });

  const onSubmit = (values: MediaUploadFormData): void => {
    uploadMediaAndTranslationsTask(mediaType, values);
  };

  return (
    <Form
      onSubmit={onSubmit}
      render={(renderProps): ReactNode => (
        <MediaUploadForm
          task={uploadMediaAndTranslationsTask}
          mediaType={mediaType}
          {...renderProps}
        />
      )}
      initialValues={getMediaUploadInitialValues()}
      validate={(values: MediaUploadFormData): ValidationErrors | undefined => ({
        file: validate(values.file, [required()]),
        folderName: validate(values.folderName, [required(), urlPartialValidation]),
        description: validate(values.description, [required()]),
        width: validate(values.width, [required()]),
        height: validate(values.height, [required()]),
      })}
      mutators={{
        ...arrayMutators,
      }}
    />
  );
};

const Container = styled(FormFields)`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  margin-top: 12px;
  overflow-x: auto;
  ${NoScrollBar};
`;

const FieldColumn = styled(Column)`
  border: 2px solid ${(props): string => props.theme.colors.gray.v3};
  border-radius: 16px;
  padding: 16px;
  margin-top: 12px;
`;

const FormButtonContainer = styled(Box)`
  margin: 24px 0;
`;



// WEBPACK FOOTER //
// ./view/components/ui/form/fields/media/media-overlay/media-picker-upload.component.tsx